import i18n from '@/i18n'
import {ChronoUnit, Duration, Instant, LocalDate, LocalDateTime, ZoneId, convert} from '@js-joda/core'
import '@js-joda/timezone'

const SYSTEM_ZONE = ZoneId.systemDefault()

function getLocale(): string {
    return i18n.language
}

/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */
export const DurationUnit = {
    HOURS: 'h',
    MINUTES: 'm',
    SECONDS: 's',
    MILLISECONDS: 'ms',
} as const;
export type DurationUnit = typeof DurationUnit[keyof typeof DurationUnit];
/* eslint-enable i18next/no-literal-string */

/**
 * Check if a date string represents a "max instant" / "never expires" value.
 * These are dates like +999999999-12-31T23:59:59.999999999Z or +10000-01-01T00:00:00Z
 * emitted by Java's `Instant.MAX`.
 *
 * Historically this function also returned `true` for any malformed string
 * (because `new Date(garbage).getTime()` is NaN). That collapsed "legitimate
 * never-expires marker" and "corrupt data" into the same UI output ("Never"),
 * which hid data bugs. The malformed case is now excluded — parseable dates
 * with year > 9999 are still treated as max-instants (the rare non-prefixed
 * form from alternate Java serializers).
 */
export function isMaxInstant(dateString: string | null | undefined): boolean {
    if (!dateString) return false

    if (dateString.startsWith('+')) {
        const yearMatch = dateString.match(/^\+(\d+)/)
        if (yearMatch) {
            const year = parseInt(yearMatch[1], 10)
            if (year >= 10000) return true
        }
    }

    const date = new Date(dateString)
    if (isNaN(date.getTime())) return false
    return date.getFullYear() > 9999
}

/**
 * Validate an expiration date string (YYYY-MM-DD).
 * Returns true if the date is today or in the future AND within maxDurationDays (if specified).
 * Returns true for empty/null values (optional expiration is valid when empty).
 * String comparison is safe because YYYY-MM-DD sorts lexicographically.
 *
 * `maxDurationDays == 0` means "today is the latest valid date" — the upper-bound
 * check must still run. The prior `if (maxDurationDays)` falsy gate treated 0
 * as "no limit", silently bypassing policies that set a zero-day maximum.
 */
export function isValidExpirationDate(expiresAt: string | null | undefined, maxDurationDays?: number | null): boolean {
    if (!expiresAt) return true
    const today = AppLocalDate.today()
    if (expiresAt < today) return false
    if (maxDurationDays != null) {
        if (expiresAt > AppLocalDate.maxFromToday(maxDurationDays)) return false
    }
    return true
}

/**
 * Parse a date string into a tagged result so callers can distinguish
 * "never expires" (a legitimate domain value rendered as "Never") from
 * "malformed input" (corrupt data / caller bug rendered as an em-dash).
 * The previous single-null return collapsed both cases together.
 */
/* eslint-disable i18next/no-literal-string -- internal parse-result tags, not user-visible */
const ParsedDisplayDateKind = {
    EMPTY: 'empty',
    MAX_INSTANT: 'max-instant',
    INVALID: 'invalid',
    VALID: 'valid',
} as const
/* eslint-enable i18next/no-literal-string */

type ParsedDisplayDate =
    | {kind: typeof ParsedDisplayDateKind.EMPTY}
    | {kind: typeof ParsedDisplayDateKind.MAX_INSTANT}
    | {kind: typeof ParsedDisplayDateKind.INVALID}
    | {kind: typeof ParsedDisplayDateKind.VALID; date: Date}

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

function parseDisplayDate(dateString: string | null | undefined): ParsedDisplayDate {
    if (!dateString) return {kind: ParsedDisplayDateKind.EMPTY}
    if (isMaxInstant(dateString)) return {kind: ParsedDisplayDateKind.MAX_INSTANT}
    // WHY: a bare YYYY-MM-DD is a calendar date — parse at LOCAL midnight, not
    // `new Date("2026-06-06")` which JS reads as UTC midnight and then renders
    // one day earlier in any negative-UTC timezone (the issue-964 off-by-one).
    if (DATE_ONLY_PATTERN.test(dateString)) {
        return {kind: ParsedDisplayDateKind.VALID, date: AppDate.ofLocalDateStr(dateString)}
    }
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return {kind: ParsedDisplayDateKind.INVALID}
    return {kind: ParsedDisplayDateKind.VALID, date}
}

function formatWithRenderer(
    dateString: string | null | undefined,
    render: (date: Date) => string,
): string {
    const parsed = parseDisplayDate(dateString)
    switch (parsed.kind) {
        case ParsedDisplayDateKind.EMPTY:
        case ParsedDisplayDateKind.INVALID:
            return '\u2014'
        case ParsedDisplayDateKind.MAX_INSTANT:
            return i18n.t('common.never')
        case ParsedDisplayDateKind.VALID:
            return render(parsed.date)
    }
}

function formatLocalDate(dateString: string | null | undefined, options: Intl.DateTimeFormatOptions): string {
    return formatWithRenderer(dateString, (date) => date.toLocaleDateString(getLocale(), options))
}

/**
 * Format a date string for display, handling max instant values.
 * Returns "Never" for max instant / "never expires" dates; em-dash for
 * empty or malformed values.
 */
export function formatDate(dateString: string | null | undefined): string {
    // WHY: a bare YYYY-MM-DD is a calendar date with no time-of-day — render it
    // date-only so a LocalDate field (e.g. certification dueDate, issue 964)
    // never shows a spurious "12:00 AM". Instant strings keep the time portion.
    if (typeof dateString === 'string' && DATE_ONLY_PATTERN.test(dateString)) {
        return formatLocalDate(dateString, { year: 'numeric', month: 'short', day: 'numeric' })
    }
    return formatLocalDate(dateString, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/**
 * Format a date string for shorter display (just date, no time).
 */
export function formatDateShort(dateString: string | null | undefined): string {
    return formatLocalDate(dateString, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function formatDateTime(dateString: string | null | undefined): string {
    return formatWithRenderer(dateString, (date) => date.toLocaleString(getLocale(), {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }))
}

export function formatTimestamp(dateString: string | null | undefined): string {
    return formatWithRenderer(dateString, (date) => date.toLocaleString(getLocale()))
}

export function formatDateOnly(dateString: string | null | undefined): string {
    return formatWithRenderer(dateString, (date) => date.toLocaleDateString(getLocale()))
}

export function formatTimeOnly(dateString: string | null | undefined): string {
    return formatWithRenderer(dateString, (date) => date.toLocaleTimeString(getLocale()))
}

/**
 * Format a date as "X ago" or "in X" relative to now. Dates in the future use
 * the "in X" form — the previous implementation computed `diffMs = now − date`
 * and only checked `> 0` thresholds, so future dates fell through to "just now"
 * regardless of distance. Dates within ± one minute render as "just now" /
 * "in a moment".
 */
export function formatRelativeTime(dateString: string | null | undefined): string {
    return formatWithRenderer(dateString, (date) => {
        const diffMs = Date.now() - date.getTime()
        const future = diffMs < 0
        const absMs = Math.abs(diffMs)
        const absMins = Math.floor(absMs / 60_000)
        const absHours = Math.floor(absMins / 60)
        const absDays = Math.floor(absHours / 24)

        if (future) {
            if (absDays > 0) return i18n.t('common.durationDaysFromNow', {count: absDays})
            if (absHours > 0) return i18n.t('common.durationHoursFromNow', {count: absHours})
            if (absMins > 0) return i18n.t('common.durationMinutesFromNow', {count: absMins})
            return i18n.t('common.inAMoment')
        }
        if (absDays > 0) return i18n.t('common.durationDaysAgo', {count: absDays})
        if (absHours > 0) return i18n.t('common.durationHoursAgo', {count: absHours})
        if (absMins >= 5) return i18n.t('common.durationMinutesAgo', {count: absMins})
        return i18n.t('common.justNow')
    })
}

export function formatNumber(value: number, decimals: number = 0): string {
    return value.toLocaleString(getLocale(), {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })
}

/**
 * Format a duration in milliseconds to a human-readable string.
 * Examples: "30s", "5m", "2h", "1h 30m"
 */
export function formatDuration(ms: number | null | undefined): string {
    if (ms == null || ms === 0) return '\u2014'

    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)

    if (hours > 0) {
        const remainingMinutes = minutes % 60
        if (remainingMinutes > 0) {
            return `${hours}${DurationUnit.HOURS} ${remainingMinutes}${DurationUnit.MINUTES}`
        }
        return `${hours}${DurationUnit.HOURS}`
    }
    if (minutes > 0) {
        const remainingSeconds = seconds % 60
        if (remainingSeconds > 0 && minutes < 10) {
            return `${minutes}${DurationUnit.MINUTES} ${remainingSeconds}${DurationUnit.SECONDS}`
        }
        return `${minutes}${DurationUnit.MINUTES}`
    }
    if (seconds > 0) {
        return `${seconds}${DurationUnit.SECONDS}`
    }
    return `${ms}${DurationUnit.MILLISECONDS}`
}

/**
 * Format an ISO-8601 duration string (Java `Duration.toString()` output) to a
 * human-readable label. Example: "PT10M" -> "10m", "PT1H30M" -> "1h 30m".
 * Returns an em-dash for null/empty — throws on unparseable input (issue 1104).
 */
export function formatIsoDuration(iso: string | null | undefined): string {
    if (iso == null || iso === '') return '\u2014'
    const millis = Duration.parse(iso).toMillis()
    return formatDuration(millis)
}

/**
 * Parse a duration string to milliseconds.
 * Supports formats: "30s", "5m", "2h", "1h30m", "1h 30m", "90" (minutes)
 */
export function parseDuration(input: string): number | null {
    if (!input || input.trim() === '') return null

    const trimmed = input.trim().toLowerCase()

    // Durations are non-negative. A leading "-" would otherwise slip past the
    // plain-number branch (parseInt('-5', 10) === -5) and emit a negative ms
    // value to onChange (Bug D from issue 398).
    if (trimmed.startsWith('-')) return null

    // Try parsing as plain number (assume minutes)
    const plainNumber = parseInt(trimmed, 10)
    if (!isNaN(plainNumber) && trimmed === String(plainNumber)) {
        return plainNumber * 60 * 1000 // Convert minutes to ms
    }

    // Parse duration string like "1h30m", "2h", "30m", "45s"
    let totalMs = 0
    const regex = /(\d+)\s*(h|m|s|ms)/g
    let match

    while ((match = regex.exec(trimmed)) !== null) {
        const value = parseInt(match[1], 10)
        const unit = match[2]

        switch (unit) {
            case DurationUnit.HOURS:
                totalMs += value * 60 * 60 * 1000
                break
            case DurationUnit.MINUTES:
                totalMs += value * 60 * 1000
                break
            case DurationUnit.SECONDS:
                totalMs += value * 1000
                break
            case DurationUnit.MILLISECONDS:
                totalMs += value
                break
        }
    }

    return totalMs > 0 ? totalMs : null
}

/**
 * Format milliseconds to an editable duration string (for input fields).
 * Returns the most compact representation: "2h", "30m", "45s"
 */
export function formatDurationForInput(ms: number | null | undefined): string {
    if (ms == null || ms === 0) return ''

    const hours = Math.floor(ms / (60 * 60 * 1000))
    const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000))
    const seconds = Math.floor((ms % (60 * 1000)) / 1000)

    const parts: string[] = []
    if (hours > 0) parts.push(`${hours}${DurationUnit.HOURS}`)
    if (minutes > 0) parts.push(`${minutes}${DurationUnit.MINUTES}`)
    if (seconds > 0 && hours === 0) parts.push(`${seconds}${DurationUnit.SECONDS}`)

    return parts.join(' ') || ''
}

// WHY: single boundary between application code and raw Date math (issue 842).
// The no-utc-date-slice ESLint rule bans raw new Date() outside this file;
// helpers below delegate to js-joda so timezone behaviour matches the Spring
// backend's java.time mental model. The scoped namespace classes
// (AppLocalDate / AppDate / AppInstant) live at the bottom of this file.

/** Local YYYY-MM-DD → ISO instant at local start-of-day. */
export function localStartOfDayIso(localDate: string): string {
    return LocalDate.parse(localDate).atStartOfDay(SYSTEM_ZONE).toInstant().toString()
}

/** Local YYYY-MM-DD → ISO instant at local end-of-day (23:59:59 local). */
export function localEndOfDayIso(localDate: string): string {
    return LocalDate.parse(localDate)
        .atTime(23, 59, 59)
        .atZone(SYSTEM_ZONE)
        .toInstant()
        .toString()
}

/**
 * Convert an ISO instant to the "YYYY-MM-DDTHH:MM" shape that
 * <input type="datetime-local"> expects, in LOCAL time.
 *
 * The native input displays its value as local wall-clock time. Feeding it the
 * raw UTC datetime (`.toISOString().slice(0, 16)`) misrenders for any user
 * outside the UTC offset — this helper fixes that.
 *
 * Throws on unparseable input (issue 861). Use {@link isValidInstantIso} at
 * user-input boundaries (URL params, localStorage) to validate before
 * calling — invalid input there is a bug the caller must surface, not
 * silently swallow.
 */
export function localDateTimeForInput(iso: string | null | undefined): string {
    if (!iso) return ''
    const ldt = LocalDateTime.ofInstant(Instant.parse(iso), SYSTEM_ZONE)
    // YYYY-MM-DDTHH:MM
    return ldt.toString().substring(0, 16)
}

/**
 * Convert an ISO instant to the "YYYY-MM-DD" local-date shape that
 * {@link DatePicker} (without `withTime`) expects, in LOCAL time.
 *
 * Mirrors {@link localDateTimeForInput} but drops the time component. Returns
 * an empty string for null/undefined input. Throws on unparseable input.
 */
export function localDateForInput(iso: string | null | undefined): string {
    if (!iso) return ''
    return LocalDate.ofInstant(Instant.parse(iso), SYSTEM_ZONE).toString()
}

/**
 * Predicate: does the string parse as a valid ISO-8601 instant?
 *
 * Use at user-input boundaries (URL params, localStorage, form values) where
 * garbage is a possibility — the caller can then surface the error via
 * `reportJavaScriptError` + a destructive toast instead of letting it crash
 * the page. Inside trusted code paths (backend response data), prefer calling
 * `Instant.parse` directly and let unexpected garbage throw.
 */
export function isValidInstantIso(value: string | null | undefined): boolean {
    if (!value) return false
    try {
        Instant.parse(value)
        return true
    } catch (e) {
        void e
        return false
    }
}

/**
 * Returns a UTC string key for "the current minute, agreed by all callers".
 * Used as a bucket key in `auth-bounce-counter` to detect session bounces
 * within the same minute. UTC (not local) so the key is stable across
 * timezone-aware test mocks of `Date.now()`.
 */
export function currentMinuteBucket(): string {
    // YYYY-MM-DDTHH:MM (UTC)
    return Instant.now().toString().substring(0, 16)
}

/**
 * Parse a "YYYY-MM-DDTHH:MM" datetime-local input value (interpreted in LOCAL
 * time) into an ISO-8601 instant. Inverse of {@link localDateTimeForInput}.
 */
export function localDateTimeToIso(value: string): string {
    return LocalDateTime.parse(value).atZone(SYSTEM_ZONE).toInstant().toString()
}

/**
 * Normalize a date-like string into an ISO-8601 instant.
 *
 * - "YYYY-MM-DD" → local start-of-day ISO instant
 * - any other parseable date string → canonical ISO instant form
 *
 * Replaces the legacy `new Date(value).toISOString()` round-trip used in
 * controller mutations whose payload may carry either a local date or a full
 * instant depending on the source.
 */
export function toInstantIso(value: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return localStartOfDayIso(value)
    }
    const parsed = new Date(value)
    if (isNaN(parsed.getTime())) {
        throw new Error(`toInstantIso: cannot parse '${value}'`)
    }
    return parsed.toISOString()
}

/** Current local year + quarter (1..4). Helper for naming campaigns / reports. */
export function currentYearQuarter(): {year: number; quarter: number} {
    const today = LocalDate.now(SYSTEM_ZONE)
    return {
        year: today.year(),
        quarter: Math.floor((today.monthValue() - 1) / 3) + 1,
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Scoped namespace classes — sole entry points for js-joda date math.
//
// WHY: HQG round on 892 + 975 asked for "static methods everywhere — static
// methods are fine, but at least give them a properly scoped root". Each call
// site reads `AppLocalDate.daysFromToday(N)` instead of a bare top-level
// function. Each class is uninstantiable.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Local-zone calendar dates as ISO `YYYY-MM-DD` strings.
 * Internally uses js-joda `LocalDate` + `SYSTEM_ZONE` so the produced strings
 * track the user's wall clock, not UTC.
 */
export class AppLocalDate {
    private constructor() {
        // namespace — not instantiable
    }

    /** Today's local date as `YYYY-MM-DD`. */
    static today(): string {
        return LocalDate.now(SYSTEM_ZONE).toString()
    }

    /** Local date N days from today as `YYYY-MM-DD`. Negative N → past date. */
    static daysFromToday(days: number): string {
        return LocalDate.now(SYSTEM_ZONE).plusDays(days).toString()
    }

    /** Local date N years from today as `YYYY-MM-DD`. */
    static yearsFromToday(years: number): string {
        return LocalDate.now(SYSTEM_ZONE).plusYears(years).toString()
    }

    /**
     * Local date N days from today as `YYYY-MM-DD`, clamped to today.
     * Use for HTML date input `max` attribute when enforcing a maximum
     * duration — negative offsets are nonsensical there and are clamped to 0.
     */
    static maxFromToday(daysFromToday: number): string {
        const days = Math.max(0, daysFromToday)
        return LocalDate.now(SYSTEM_ZONE).plusDays(days).toString()
    }
}

/**
 * JS `Date` boundary objects — only where the runtime requires a Date
 * (shadcn `Calendar` `minDate` / `maxDate`, Vitest's `setSystemTime`,
 * external libraries). Internally threads js-joda + `SYSTEM_ZONE`.
 */
export class AppDate {
    private constructor() {
        // namespace — not instantiable
    }

    /** Current time as a JS Date. */
    static now(): Date {
        return convert(Instant.now()).toDate()
    }

    /** Parse an ISO-8601 instant into a JS Date. */
    static ofInstant(iso: string): Date {
        return convert(Instant.parse(iso)).toDate()
    }

    /**
     * Parse a local `YYYY-MM-DD` date into a JS Date at local midnight.
     * Use for DatePicker `minDate` / `maxDate` props.
     */
    static ofLocalDateStr(localDate: string): Date {
        return convert(LocalDate.parse(localDate).atStartOfDay(SYSTEM_ZONE)).toDate()
    }

    /** Epoch milliseconds for an ISO-8601 instant. */
    static epochMillis(iso: string): number {
        return Instant.parse(iso).toEpochMilli()
    }
}

/**
 * ISO-8601 instants (`YYYY-MM-DDTHH:mm:ssZ`). Internally js-joda `Instant`.
 * Instants are always UTC by definition — local-zone math goes through
 * {@link AppLocalDate}.
 */
export class AppInstant {
    private constructor() {
        // namespace — not instantiable
    }

    /** Current instant as an ISO-8601 string (e.g. `"2026-05-18T08:00:00Z"`). */
    static now(): string {
        return Instant.now().toString()
    }

    /** ISO instant N days from now. Negative N → past instant. */
    static daysFromNow(days: number): string {
        return Instant.now().plus(days, ChronoUnit.DAYS).toString()
    }

    /** ISO instant N milliseconds from now. Negative N → past instant. */
    static millisFromNow(ms: number): string {
        return Instant.now().plus(ms, ChronoUnit.MILLIS).toString()
    }

    /** ISO instant + N days, as an ISO string. Negative N → past instant. */
    static addDays(iso: string, days: number): string {
        return Instant.parse(iso).plus(days, ChronoUnit.DAYS).toString()
    }
}
